import { cn } from '@ntconsult/aluma-react';
import { useTranslation } from 'react-i18next';
import type { PreviaConvite } from '../types/Convites';

type Props = {
  /** A última prévia válida; sem ela, só o espaço. */
  previa: PreviaConvite | undefined;
  /** Uma prévia nova está a caminho: a atual fica esmaecida (R11, sem `Spinner`). */
  atualizando?: boolean;
  /** Linha "Para", só na prévia por pessoa (R18). */
  para?: string;
  className?: string;
};

/** Altura do `body` (o `documentElement` não encolhe), depois de zerar a do `iframe` (R9). */
function medir(f: HTMLIFrameElement) {
  const body = f.contentDocument?.body;
  if (!body) return;
  f.style.height = '0px';
  // `border-box`: a altura do `iframe` inclui a borda.
  f.style.height = `${body.scrollHeight + f.offsetHeight - f.clientHeight}px`;
}

/**
 * O e-mail como chega, em tamanho real (spec 30 R9): o HTML vem da API, a mesma função do envio.
 * `allow-same-origin` sem `allow-scripts`: o HTML não executa nada e a página mede o conteúdo e
 * bloqueia os links.
 */
export function PreviaAoVivo({ previa, atualizando = false, para, className }: Props) {
  const { t } = useTranslation('convites');

  // Remede quando a largura muda (abaixo de 600 px a altura acompanha).
  const observar = (f: HTMLIFrameElement | null) => {
    if (!f) return;
    let largura = f.clientWidth;
    const ro = new ResizeObserver(() => {
      if (f.clientWidth === largura) return;
      largura = f.clientWidth;
      medir(f);
    });
    ro.observe(f);
    return () => ro.disconnect();
  };

  return (
    <div className={cn('flex w-full max-w-[600px] flex-col gap-2', className)}>
      {previa && (
        <dl className="grid grid-cols-[5rem_1fr] gap-y-1 text-sm">
          {previa.remetente && (
            <>
              <dt className="text-foreground-muted">{t('previa.de')}</dt>
              <dd className="break-all">{previa.remetente}</dd>
            </>
          )}
          {para !== undefined && (
            <>
              <dt className="text-foreground-muted">{t('previa.para')}</dt>
              <dd className="break-all">{para}</dd>
            </>
          )}
          <dt className="text-foreground-muted">{t('previa.assunto')}</dt>
          <dd className="break-words" data-previa-assunto>
            {previa.assunto}
          </dd>
        </dl>
      )}
      <iframe
        ref={observar}
        title={t('previa.titulo')}
        sandbox="allow-same-origin"
        srcDoc={previa?.html ?? ''}
        scrolling="no"
        onLoad={(e) => {
          const f = e.currentTarget;
          f.contentDocument?.addEventListener('click', (ev) => ev.preventDefault());
          medir(f);
        }}
        className={cn(
          'block w-full rounded border border-border bg-white transition-opacity',
          atualizando && 'opacity-60',
        )}
      />
    </div>
  );
}
